import { Link } from 'react-router-dom';
import { useLang } from '@/shared/i18n/useT';
import { InlineSVG } from './InlineSVG';

interface FooterLink {
  label: string;
  to?: string;
  href?: string;
  external?: boolean;
}

export function Footer() {
  const { lang } = useLang();

  const links: FooterLink[] =
    lang === 'en'
      ? [
          { label: 'Sign in', href: 'https://app.bundleport.com', external: true },
          { label: 'About us', to: '/about' },
          { label: 'API Platform', to: '/api-platform' },
        ]
      : [
          { label: 'Acceder', href: 'https://app.bundleport.com', external: true },
          { label: 'Sobre nosotros', to: '/about' },
          { label: 'Plataforma API', to: '/api-platform' },
        ];

  const legal: FooterLink[] =
    lang === 'en'
      ? [
          { label: 'Terms of service', to: '/terms-of-service' },
          { label: 'Privacy policy', to: '/privacy-policy' },
        ]
      : [
          { label: 'Términos de servicio', to: '/terms-of-service' },
          { label: 'Política de privacidad', to: '/privacy-policy' },
        ];

  return (
    <footer className="bp-foot tone-soft">
      <div className="ft-inner">
        <div className="ft-brand">
          <InlineSVG
            className="ft-logo"
            src="/assets/logo-bundleport.svg"
            alt="Bundleport"
            style={{ display: 'inline-block', width: 168, marginBottom: 20 }}
          />
          <p className="ft-tagline">
            All Species. One <em className="t-serif-italic">Ecosystem</em>.
          </p>
        </div>
        <div className="ft-col">
          <p className="ft-col-heading">{lang === 'en' ? 'LINKS' : 'ENLACES'}</p>
          {links.map((l) =>
            l.to ? (
              <Link key={l.label} to={l.to}>
                {l.label}
              </Link>
            ) : (
              <a
                key={l.label}
                href={l.href}
                target={l.external ? '_blank' : undefined}
                rel={l.external ? 'noopener noreferrer' : undefined}
              >
                {l.label}
              </a>
            ),
          )}
        </div>
        <div className="ft-col">
          <p className="ft-col-heading">SOCIAL MEDIA</p>
          <div className="ft-socials">
            <a
              href="https://www.linkedin.com/company/bundleport-travel"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn"
            >
              <InlineSVG
                src="/assets/icons/linkedin.svg"
                alt=""
                color="currentColor"
                style={{ width: 18, height: 18, display: 'block' }}
              />
            </a>
            <a
              href="https://www.instagram.com/bundleport.app"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
            >
              <InlineSVG
                src="/assets/icons/instagram.svg"
                alt=""
                color="currentColor"
                style={{ width: 18, height: 18, display: 'block' }}
              />
            </a>
          </div>
        </div>
      </div>
      <div
        className="ft-funding"
        role="contentinfo"
        aria-label={lang === 'en' ? 'EU funding' : 'Financiación UE'}
      >
        <img
          className="ft-funding-logos"
          src="/assets/funding-eu-prtr.png"
          alt={
            lang === 'en'
              ? 'Funded by the European Union – NextGenerationEU, Government of Spain – Ministry for Digital Transformation and the Civil Service, SETT, and Recovery, Transformation and Resilience Plan'
              : 'Financiado por la Unión Europea – NextGenerationEU, Gobierno de España – Ministerio para la Transformación Digital y de la Función Pública, SETT, y Plan de Recuperación, Transformación y Resiliencia'
          }
        />
        <p className="ft-funding-text">
          {lang === 'en'
            ? 'This company is participated by Sociedad Española para la Transformación Tecnológica, a public business entity (SETT), within the Recovery, Transformation and Resilience Plan funded by the European Union.'
            : 'Esta empresa está participada por la Sociedad Española para la Transformación Tecnológica, entidad pública empresarial, SETT, en el marco del Plan de Recuperación, Transformación y Resiliencia financiado por la Unión Europea.'}
        </p>
      </div>
      <div className="ft-bar">
        <span>
          © 2026 Bundleport.{' '}
          {lang === 'en' ? 'All rights reserved.' : 'Todos los derechos reservados.'}
        </span>
        <span className="ft-legal">
          {legal.map((l, i) => (
            <Link key={l.label} to={l.to ?? '/'}>
              {l.label}
              {i < legal.length - 1 ? ' · ' : ''}
            </Link>
          ))}
        </span>
      </div>
    </footer>
  );
}
